import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { MapPinOff, PawPrint, SlidersHorizontal, Ticket, WifiOff, X } from 'lucide-react';

import { type PropertyType, propertyTypes } from 'src/api/landlord-properties-api';
import MapTileSwitcher from 'src/components/map-tile-switcher';
import NumberField from 'src/components/input/number-field/NumberField';
import PropertyDetailsCard, {
    formatOccupiedUntil,
    formatPropertySummary,
    getOccupiedUntil,
    isFreeingSoon,
} from 'src/components/property-details-card';
/*
 * Leaf imports rather than the `src/services/api` barrel: the barrel re-exports every
 * API module, including some that construct request URLs at module-load time, so
 * pulling it in from a component mountable outside the full app shell (see
 * SimilarPropertiesContext) risks eagerly evaluating modules this one never touches.
 */
import { useProperties } from 'src/services/api/properties/use-properties';
import type { IProperty } from 'src/services/api/properties/types';
import { VOUCHER_TYPE_OPTIONS } from 'src/services/api/leads/lead-facts';
import {
    diagnoseEmptyResult,
    explainFit,
    findSimilarProperties,
    formatBathsFragment,
    formatBedsFragment,
    formatDistance,
    formatRentFragment,
    formatSearchSentence,
    getAnchorPosition,
    getDefaultFilters,
    getLeadSignals,
    RADIUS_OPTIONS,
    type ILeadSignals,
    type ISimilarityFilters,
} from 'src/components/similar-properties-modal/find-similar-properties';
import { buildPropertyPitch } from 'src/components/similar-properties-modal/build-property-pitch';
import { typeIconUrl } from 'src/components/property-details-card/property-type-icons';

import 'leaflet/dist/leaflet.css';
import 'src/styles/map-surface.css';
import styles from 'src/components/similar-properties-modal/SimilarPropertiesModal.module.css';

const ANCHOR_COLOR = '#d97706';
const MILES_TO_METRES = 1609.34;

// Continental US, used when there is no anchor to centre on.
const FALLBACK_CENTER: L.LatLngTuple = [39.5, -98.35];
const FALLBACK_ZOOM = 4;
const ANCHOR_ZOOM = 13;

/**
 * Leaflet handles are kept together in state rather than in refs so that the effects
 * which draw markers re-run once the map exists. With refs those effects run before the
 * map is created and never again, leaving a map with tiles but nothing on it.
 */
type IMapHandles = {
    map: L.Map;
    markers: L.LayerGroup;
};

type Props = {
    /**
     * The property the lead is assigned to. Matches are ranked around it. Null when the
     * lead has no property, in which case the map shows the team's vacant inventory.
     */
    anchor: IProperty | null;
    /**
     * Budget and pet signals for ranking and hard-filtering. Absent for standalone use
     * (e.g. from the properties page), where budget ranking falls back to the anchor's
     * own rent and there is no pet to filter on.
     */
    signals?: ILeadSignals;
    /**
     * Property to open selected, for when the modal is entered from a specific match in
     * the sidebar rather than from the map as a whole.
     */
    initialActiveId?: number | null;
    /**
     * Appends text to the lead-conversation composer. When present, the details card
     * offers "Add to message" and the modal closes on insert so the agent lands back
     * on the draft. Absent outside the messages page.
     */
    onInsertMessage?: (text: string) => void;
    onClose: () => void;
};

export default function SimilarPropertiesModal({
    anchor,
    signals,
    initialActiveId = null,
    onInsertMessage,
    onClose,
}: Props) {
    const { data: properties = [], isLoading, isError } = useProperties();

    const [filters, setFilters] = useState<ISimilarityFilters>(() => getDefaultFilters(anchor));
    const [activeId, setActiveId] = useState<number | null>(initialActiveId);
    const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);
    const [isTileError, setTileError] = useState(false);

    // standalone use (no lead in the picture) still ranks by the anchor's own rent
    const effectiveSignals = useMemo(
        () => signals ?? getLeadSignals(null, anchor),
        [signals, anchor]
    );

    /*
     * The container is tracked in state rather than a ref so that map setup runs when
     * the node actually attaches. With a ref, the setup effect has nothing that
     * changes once it mounts, so a container that was not ready on the first pass
     * would never get a map.
     */
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const [handles, setHandles] = useState<IMapHandles | null>(null);
    const resultRefs = useRef(new Map<number, HTMLButtonElement>());

    const anchorPosition = getAnchorPosition(anchor);
    const isDistanceRanked = anchorPosition != null;

    const { matches, skippedWithoutCoordinates } = useMemo(
        () => findSimilarProperties(anchor, properties, filters, effectiveSignals),
        [anchor, properties, filters, effectiveSignals]
    );

    /*
     * One pass over matches rather than a recompute per row: explainFit re-derives the
     * same thresholds scoreSimilarity already weighed, so it is worth doing once here
     * instead of inline in each row's render.
     */
    const fitReasonsById = useMemo(() => new Map(
        matches.map(({ property, distanceMiles }) => (
            [property.id, explainFit(property, distanceMiles, anchor, effectiveSignals)]
        ))
    ), [matches, anchor, effectiveSignals]);

    const activeProperty = useMemo(() => {
        if (activeId == null) {
            return null;
        }
        if (anchor != null && anchor.id === activeId) {
            return anchor;
        }
        return matches.find(({ property }) => property.id === activeId)?.property ?? null;
    }, [activeId, anchor, matches]);

    const activeDistance = useMemo(
        () => matches.find(({ property }) => property.id === activeId)?.distanceMiles ?? null,
        [activeId, matches]
    );

    // present only for an actual match — the anchor itself never appears in fitReasonsById
    const activeFitReasons = activeProperty != null ? fitReasonsById.get(activeProperty.id) ?? [] : [];

    const handleTileError = useCallback(() => setTileError(true), []);

    const patchFilters = useCallback((patch: Partial<ISimilarityFilters>) => {
        setFilters((current) => ({ ...current, ...patch }));
    }, []);

    /*
     * The filters tucked behind "More filters" surface here as removable chips instead
     * of a bare count badge on the button — the chips themselves state, and let the
     * agent undo, what is narrowing the search.
     */
    type IFilterChip = { key: 'rent' | 'beds' | 'baths'; label: string; patch: Partial<ISimilarityFilters> };
    const rentFragment = formatRentFragment(filters);
    const bedsFragment = formatBedsFragment(filters);
    const bathsFragment = formatBathsFragment(filters);
    const filterChips: (IFilterChip | null)[] = [
        rentFragment != null ?
            { key: 'rent', label: `Rent ${rentFragment}`, patch: { rentMin: null, rentMax: null } } :
            null,
        bedsFragment != null ?
            { key: 'beds', label: bedsFragment, patch: { bedroomsMin: null, bedroomsMax: null } } :
            null,
        bathsFragment != null ?
            { key: 'baths', label: bathsFragment, patch: { bathroomsMin: null } } :
            null,
    ];
    const activeFilterChips = filterChips.filter((chip): chip is IFilterChip => chip != null);

    const emptyFixes = useMemo(
        () => (matches.length === 0 ? diagnoseEmptyResult(anchor, properties, filters, effectiveSignals) : []),
        [matches.length, anchor, properties, filters, effectiveSignals]
    );

    useEffect(() => {
        if (!container) {
            return;
        }

        const map = L.map(container, {
            preferCanvas: true,
            center: anchorPosition ?
                [anchorPosition.latitude, anchorPosition.longitude] :
                FALLBACK_CENTER,
            zoom: anchorPosition ? ANCHOR_ZOOM : FALLBACK_ZOOM,
            zoomControl: false,
        });
        L.control.zoom({ position: 'bottomright' }).addTo(map);

        // the base tile layer is owned by MapTileSwitcher
        const markers = L.layerGroup().addTo(map);

        /*
         * The dialog animates in, so the container is still zero-height when Leaflet
         * first measures it and no tiles get requested. Watching the element covers
         * both the animation and later resizes; a one-shot timeout does not.
         */
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(container);

        setHandles({ map, markers });

        return () => {
            observer.disconnect();
            map.remove();
            setHandles(null);
        };
        // the anchor position only seeds the initial view, so it is deliberately not a dep
    }, [container]);

    // Drop the selection when the property it pointed at falls outside the new filters.
    useEffect(() => {
        setActiveId((current) => {
            if (current == null || (anchor != null && current === anchor.id)) {
                return current;
            }
            return matches.some(({ property }) => property.id === current) ? current : null;
        });
    }, [matches, anchor]);

    // Redraw the radius, the anchor and every match whenever the filters change.
    useEffect(() => {
        if (!handles) {
            return;
        }
        const { markers } = handles;

        markers.clearLayers();

        if (anchorPosition) {
            /*
             * The search radius. Amber like the anchor it belongs to, with a stroke and
             * tint strong enough to survive OSM's busy street colouring — the previous
             * hairline at 5% fill disappeared into the map.
             */
            L.circle([anchorPosition.latitude, anchorPosition.longitude], {
                radius: filters.radiusMiles * MILES_TO_METRES,
                color: ANCHOR_COLOR,
                weight: 3,
                opacity: 0.9,
                dashArray: '10 8',
                fillColor: ANCHOR_COLOR,
                fillOpacity: 0.08,
                interactive: false,
            }).addTo(markers);
        }

        // the same type-icon pins the properties-page map uses, so both maps read alike
        const pinIcon = (property: IProperty, extraClass: string) => L.divIcon({
            className: '',
            html: `<span class="map-pin ${property.is_vacant ? 'map-pin-vacant' : ''} ${extraClass}"><img src="${typeIconUrl(property.type)}" alt="" class="map-pin-img" /></span>`,
            iconSize: [30, 30],
            iconAnchor: [15, 15],
        });

        for (const { property, distanceMiles } of matches) {
            const marker = L.marker(
                [property.latitude as number, property.longitude as number],
                { icon: pinIcon(property, property.id === activeId ? 'map-pin-active' : '') }
            );
            const tooltip = [
                property.name,
                formatPropertySummary(property),
                distanceMiles != null ? formatDistance(distanceMiles) : null,
            ].filter(Boolean).join(' · ');
            marker.bindTooltip(tooltip, { direction: 'top' });
            marker.on('click', () => setActiveId(property.id));
            marker.addTo(markers);
        }

        // drawn last so the assigned property always sits on top of the matches
        if (anchor != null && anchorPosition) {
            L.marker([anchorPosition.latitude, anchorPosition.longitude], {
                icon: pinIcon(anchor, 'map-pin-anchor'),
                zIndexOffset: 1000,
            })
                .bindTooltip(`${anchor.name} (this lead)`, {
                    // auto keeps the label off the details panel in the top-right corner
                    direction: 'auto',
                    permanent: true,
                    className: 'similar-anchor-tooltip',
                })
                .on('click', () => setActiveId(anchor.id))
                .addTo(markers);
        }
    }, [handles, matches, activeId, filters.radiusMiles, anchor, anchorPosition]);

    // Keep the viewport on the current answer without fighting manual panning.
    const boundsKey = `${filters.radiusMiles}:${matches.length}`;
    useEffect(() => {
        if (!handles) {
            return;
        }
        const { map } = handles;

        const points: L.LatLngTuple[] = matches.map(
            ({ property }) => [property.latitude as number, property.longitude as number]
        );
        if (anchorPosition) {
            points.push([anchorPosition.latitude, anchorPosition.longitude]);
        }

        if (points.length === 0) {
            map.setView(FALLBACK_CENTER, FALLBACK_ZOOM);
            return;
        }

        if (points.length === 1 && !anchorPosition) {
            map.setView(points[0], 15);
            return;
        }

        const bounds = L.latLngBounds(points);
        if (anchorPosition) {
            /*
             * Frame the search circle, not just the matches: the radius the agent picked
             * is the question being asked, and fitting only the answers used to leave the
             * circle's edge off screen — it read as a faint tint instead of a boundary.
             */
            bounds.extend(
                L.latLng(anchorPosition.latitude, anchorPosition.longitude)
                    .toBounds(filters.radiusMiles * MILES_TO_METRES * 2)
            );
        }
        map.fitBounds(bounds, { padding: [24, 24], maxZoom: 15 });
        // recentring is keyed on the shape of the answer, not on every marker redraw
    }, [handles, boundsKey]);

    /*
     * Entered from a specific match in the sidebar: frame that property instead of the
     * whole result set. Declared after the bounds effect so it wins on the first paint.
     */
    useEffect(() => {
        if (!handles || initialActiveId == null) {
            return;
        }
        const match = matches.find(({ property }) => property.id === initialActiveId);
        if (match) {
            handles.map.setView(
                [match.property.latitude as number, match.property.longitude as number],
                15
            );
        }
    }, [handles, initialActiveId, matches]);

    /*
     * The details card opens over the map, far from the list row it belongs to. Pulling
     * the row into view keeps the two halves of the selection visibly connected when the
     * selection was made by clicking a marker.
     */
    useEffect(() => {
        if (activeId == null) {
            return;
        }
        resultRefs.current.get(activeId)?.scrollIntoView({ block: 'nearest' });
    }, [activeId]);

    const handleSelectResult = (property: IProperty) => {
        setActiveId(property.id);
        const map = handles?.map;
        if (map) {
            map.setView(
                [property.latitude as number, property.longitude as number],
                Math.max(map.getZoom(), 15)
            );
        }
    };

    const searchSentence = formatSearchSentence(anchor, filters, matches, isDistanceRanked, effectiveSignals);

    return (
        <Dialog open onClose={onClose} PaperProps={{ className: styles.paper }} maxWidth={false}>
            <DialogTitle className={styles.dialogTitle}>
                <span className={styles.titleText}>
                    <Typography variant="caption" className={styles.titleEyebrow}>
                        {anchor ? 'Similar properties near' : 'Team inventory'}
                    </Typography>
                    <span className={styles.titleAnchor}>
                        {anchor && <span className={styles.anchorDot} />}
                        <Typography
                            variant="h6"
                            className={styles.titleAnchorName}
                            title={anchor?.name}
                        >
                            {anchor ? anchor.name : 'Available properties'}
                        </Typography>
                    </span>
                </span>
                <IconButton aria-label="Close similar properties" onClick={onClose} data-testid="close-similar-properties-button">
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            {/*
              * A horizontal toolbar rather than the old 244px rail: distance and vacancy
              * are the only filters touched on most visits, and the rail was spending a
              * third of the dialog's width on controls the anchor already pre-filled.
              */}
            <div className={styles.toolbar}>
                {isDistanceRanked && (
                    <div className={styles.toolbarGroup}>
                        <Typography variant="caption" className={styles.toolbarLabel}>
                            Distance
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={filters.radiusMiles}
                            onChange={(_event, miles) => {
                                if (miles != null) {
                                    patchFilters({ radiusMiles: miles as number });
                                }
                            }}
                            className={styles.radiusGroup}
                        >
                            {RADIUS_OPTIONS.map((miles) => (
                                <ToggleButton
                                    key={miles}
                                    value={miles}
                                    className={styles.radiusButton}
                                    data-testid={`similar-radius-${miles}`}
                                >
                                    {miles} mi
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </div>
                )}

                <div className={styles.toolbarGroup}>
                    <Typography variant="caption" className={styles.toolbarLabel}>
                        Type
                    </Typography>
                    <Select
                        value={filters.propertyType ?? ''}
                        onChange={(event) => {
                            const value = event.target.value as string;
                            patchFilters({
                                propertyType: value === '' ? null : value as PropertyType,
                            });
                        }}
                        size="small"
                        className={styles.typeSelect}
                        displayEmpty
                        inputProps={{
                            'aria-label': 'Property type',
                            'data-testid': 'similar-type-select',
                        }}
                    >
                        <MenuItem value="">Any type</MenuItem>
                        {propertyTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </Select>
                </div>

                <FormControlLabel
                    className={styles.vacantToggle}
                    control={
                        <Switch
                            size="small"
                            checked={filters.isVacantOnly}
                            onChange={(_event, checked) => patchFilters({ isVacantOnly: checked })}
                            data-testid="similar-vacant-only"
                        />
                    }
                    label={<Typography variant="body2">Vacant only</Typography>}
                />

                <Button
                    size="small"
                    startIcon={<SlidersHorizontal size={14} />}
                    onClick={(event) => setFiltersAnchorEl(event.currentTarget)}
                    data-testid="similar-more-filters"
                >
                    More filters
                </Button>

                <span className={styles.toolbarSpacer} />

                <Button
                    size="small"
                    onClick={() => setFilters(getDefaultFilters(anchor))}
                    data-testid="similar-reset-filters"
                >
                    {anchor ? 'Reset to similar' : 'Reset filters'}
                </Button>

                {/*
                  * Chips live on their own row below the controls, so appearing and
                  * disappearing never moves "More filters" / "Reset to similar" around.
                  */}
                {(activeFilterChips.length > 0 ||
                    effectiveSignals.petType != null ||
                    effectiveSignals.voucherType != null) && (
                    <div className={styles.chipsRow}>
                        {activeFilterChips.map((chip) => (
                            <span key={chip.key} className={styles.filterChip} data-testid={`similar-filter-chip-${chip.key}`}>
                                {chip.label}
                                <IconButton
                                    size="small"
                                    aria-label={`Remove ${chip.label} filter`}
                                    className={styles.filterChipRemove}
                                    onClick={() => patchFilters(chip.patch)}
                                >
                                    <X size={12} />
                                </IconButton>
                            </span>
                        ))}

                        {effectiveSignals.petType != null && (
                            <Tooltip title="Properties whose pet policy clearly excludes pets are hidden. Properties with an unknown policy are still shown.">
                                <span className={styles.systemRuleChip} data-testid="similar-pet-filter">
                                    <PawPrint size={13} />
                                    Pet-friendly only · {effectiveSignals.petType}
                                </span>
                            </Tooltip>
                        )}

                        {effectiveSignals.voucherType != null && (
                            <Tooltip title="Properties that explicitly don't accept this voucher are hidden. Properties with unknown voucher acceptance are still shown.">
                                <span className={styles.systemRuleChip} data-testid="similar-voucher-filter">
                                    <Ticket size={13} />
                                    Accepts {
                                        VOUCHER_TYPE_OPTIONS.find((option) => option.value === effectiveSignals.voucherType)?.label ??
                                            effectiveSignals.voucherType
                                    } only
                                </span>
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>

            {anchor == null && (
                <Typography variant="caption" className={styles.modeNote} data-testid="similar-mode-note">
                    This lead has no assigned property, so results are not distance-ranked
                    — showing all vacant team inventory.
                </Typography>
            )}

            <Popover
                open={filtersAnchorEl != null}
                anchorEl={filtersAnchorEl}
                onClose={() => setFiltersAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { className: styles.filtersPopover } }}
            >
                <div className={styles.filterGroup}>
                    <Typography variant="caption" className={styles.toolbarLabel}>Rent</Typography>
                    <div className={styles.filterPair}>
                        <NumberField
                            type="number"
                            label="Min"
                            size="small"
                            value={filters.rentMin ?? ''}
                            onChange={(event) => patchFilters({
                                rentMin: event.target.value === '' ? null : Number(event.target.value),
                            })}
                            InputLabelProps={{ shrink: true }}
                            data-testid="similar-rent-min"
                        />
                        <NumberField
                            type="number"
                            label="Max"
                            size="small"
                            value={filters.rentMax ?? ''}
                            onChange={(event) => patchFilters({
                                rentMax: event.target.value === '' ? null : Number(event.target.value),
                            })}
                            InputLabelProps={{ shrink: true }}
                            data-testid="similar-rent-max"
                        />
                    </div>
                </div>

                <div className={styles.filterGroup}>
                    <Typography variant="caption" className={styles.toolbarLabel}>Bedrooms</Typography>
                    <div className={styles.filterPair}>
                        <NumberField
                            type="number"
                            label="Min"
                            size="small"
                            value={filters.bedroomsMin ?? ''}
                            onChange={(event) => patchFilters({
                                bedroomsMin: event.target.value === '' ? null : Number(event.target.value),
                            })}
                            InputLabelProps={{ shrink: true }}
                            data-testid="similar-beds-min"
                        />
                        <NumberField
                            type="number"
                            label="Max"
                            size="small"
                            value={filters.bedroomsMax ?? ''}
                            onChange={(event) => patchFilters({
                                bedroomsMax: event.target.value === '' ? null : Number(event.target.value),
                            })}
                            InputLabelProps={{ shrink: true }}
                            data-testid="similar-beds-max"
                        />
                    </div>
                </div>

                <div className={styles.filterGroup}>
                    <Typography variant="caption" className={styles.toolbarLabel}>Bathrooms</Typography>
                    <NumberField
                        type="number"
                        label="Min"
                        size="small"
                        value={filters.bathroomsMin ?? ''}
                        onChange={(event) => patchFilters({
                            bathroomsMin: event.target.value === '' ? null : Number(event.target.value),
                        })}
                        InputLabelProps={{ shrink: true }}
                        data-testid="similar-baths-min"
                    />
                </div>
            </Popover>

            <DialogContent className={styles.body}>
                <div className={styles.mapPane}>
                    <div ref={setContainer} className={styles.map} />

                    <MapTileSwitcher
                        map={handles?.map ?? null}
                        onTileError={handleTileError}
                        className={styles.tileSwitcher}
                    />

                    <div className={styles.mapNotices}>
                        {anchor != null && !isDistanceRanked && (
                            <Typography
                                variant="caption"
                                className={`${styles.mapNotice} ${styles.mapNoticeWarn}`}
                                data-testid="similar-no-coordinates"
                            >
                                <MapPinOff size={13} />
                                {anchor.name} is not geocoded, so results are not ranked by distance.
                            </Typography>
                        )}
                        {isTileError && (
                            <Typography
                                variant="caption"
                                className={`${styles.mapNotice} ${styles.mapNoticeWarn}`}
                                data-testid="similar-tile-error"
                            >
                                <WifiOff size={13} />
                                Map tiles failed to load. Markers are still accurate.
                            </Typography>
                        )}
                    </div>

                    <div className={styles.legend}>
                        {isDistanceRanked && (
                            <Typography variant="caption" className={styles.legendItem}>
                                <span className={`${styles.legendSwatch} ${styles.legendAnchor}`} />
                                This lead&apos;s property
                            </Typography>
                        )}
                        <Typography variant="caption" className={styles.legendItem}>
                            <span className={`${styles.legendSwatch} ${styles.legendMatch}`} />
                            {isDistanceRanked ? 'Match' : 'Available'}
                        </Typography>
                    </div>

                    {/*
                      * A still-loading fetch would otherwise be indistinguishable from a
                      * genuine "nothing matches", which is the difference between "widen
                      * your filters" and "wait a second".
                      */}
                    {(isLoading || isError || matches.length === 0) && (
                        <div className={styles.mapOverlay} data-testid="similar-map-overlay">
                            <Typography variant="subtitle2" fontWeight={600}>
                                {isLoading ? 'Loading properties…' : null}
                                {isError ? 'Could not load properties' : null}
                                {!isLoading && !isError ? 'Nothing to plot' : null}
                            </Typography>
                            {isError && (
                                <Typography variant="body2" color="text.secondary">
                                    Retry from the sidebar, or reload the page.
                                </Typography>
                            )}
                            {!isLoading && !isError && (
                                <Typography variant="body2" color="text.secondary">
                                    {isDistanceRanked ?
                                        `No matches within ${filters.radiusMiles} mi.` :
                                        'No properties match these filters.'}
                                </Typography>
                            )}
                        </div>
                    )}

                    {activeProperty && (
                        <div className={styles.detailsPanel} data-testid="similar-details-panel">
                            <PropertyDetailsCard
                                property={activeProperty}
                                badge={activeDistance != null ? formatDistance(activeDistance) : undefined}
                                onClose={() => setActiveId(null)}
                                onAddToMessage={onInsertMessage ?
                                    () => {
                                        onInsertMessage(buildPropertyPitch(activeProperty));
                                        // land the agent back on the draft they just extended
                                        onClose();
                                    } :
                                    undefined}
                            />
                            {activeFitReasons.length > 0 && (
                                <div className={styles.fitReasons} data-testid="similar-fit-reasons">
                                    <Typography variant="caption" className={styles.fitReasonsHeader}>
                                        Why this fits
                                    </Typography>
                                    {activeFitReasons.map((reason) => (
                                        <Typography
                                            key={reason}
                                            variant="caption"
                                            className={styles.fitReason}
                                        >
                                            <span className={styles.fitReasonDot} />
                                            {reason}
                                        </Typography>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.results}>
                    <div className={styles.resultsHeader}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            data-testid="similar-results-count"
                        >
                            {matches.length} {matches.length === 1 ? 'match' : 'matches'}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid="similar-search-sentence"
                        >
                            {searchSentence}
                        </Typography>
                        {skippedWithoutCoordinates > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                {skippedWithoutCoordinates} not geocoded, so left off the map
                            </Typography>
                        )}
                    </div>
                    <div className={styles.resultsList}>
                        {matches.map(({ property, distanceMiles }, i) => {
                            const occupiedUntil = getOccupiedUntil(property);
                            // "best" is only a meaningful claim when the row beat someone
                            const isBestFit = i === 0 && matches.length > 1;
                            const reasons = fitReasonsById.get(property.id) ?? [];
                            const row = (
                                <button
                                    key={property.id}
                                    type="button"
                                    ref={(node) => {
                                        if (node) {
                                            resultRefs.current.set(property.id, node);
                                        } else {
                                            resultRefs.current.delete(property.id);
                                        }
                                    }}
                                    className={property.id === activeId ?
                                        `${styles.result} ${styles.resultActive}` :
                                        styles.result}
                                    onClick={() => handleSelectResult(property)}
                                    data-testid={`similar-result-${property.id}`}
                                >
                                    <span className={styles.resultNameRow}>
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            className={styles.resultName}
                                        >
                                            {property.name}
                                        </Typography>
                                        {isBestFit && (
                                            <Tooltip title={reasons[0] ?? 'Closest fit to this lead\'s budget and floor plan.'}>
                                                <span
                                                    className={styles.bestFitBadge}
                                                    data-testid="similar-best-fit"
                                                >
                                                    Best fit
                                                </span>
                                            </Tooltip>
                                        )}
                                    </span>
                                    {/* summary left, distance right-aligned on the summary's
                                        last line — the one carrying the rent */}
                                    <span className={styles.resultMetaRow}>
                                        <Typography
                                            variant="caption"
                                            component="span"
                                            className={styles.resultMeta}
                                        >
                                            {formatPropertySummary(property)}
                                        </Typography>
                                        {distanceMiles != null && (
                                            <Typography
                                                variant="caption"
                                                component="span"
                                                className={styles.resultDistance}
                                            >
                                                {formatDistance(distanceMiles)}
                                            </Typography>
                                        )}
                                    </span>
                                    {occupiedUntil != null && (
                                        <Typography
                                            variant="caption"
                                            component="span"
                                            className={isFreeingSoon(occupiedUntil) ?
                                                `${styles.resultMeta} ${styles.resultFreeingSoon}` :
                                                styles.resultMeta}
                                            data-testid={`occupied-until-${property.id}`}
                                        >
                                            Occupied until {formatOccupiedUntil(occupiedUntil)}
                                        </Typography>
                                    )}
                                </button>
                            );
                            return reasons.length > 0 ? (
                                <Tooltip
                                    key={property.id}
                                    title={reasons.join(' · ')}
                                    placement="left"
                                    enterDelay={400}
                                >
                                    {row}
                                </Tooltip>
                            ) : row;
                        })}
                        {matches.length === 0 && (
                            <div className={styles.resultsEmpty} data-testid="similar-results-empty">
                                <Typography variant="body2">
                                    {emptyFixes.length === 0 ?
                                        (isDistanceRanked ?
                                            'No vacant properties are close enough to this one.' :
                                            'No vacant properties in the team’s inventory match at all.') :
                                        (isDistanceRanked ?
                                            `Nothing matches within ${filters.radiusMiles} mi.` :
                                            'Nothing matches these filters.')}
                                </Typography>
                                {emptyFixes.length > 0 && (
                                    <div className={styles.emptyFixes}>
                                        {emptyFixes.map((fix) => (
                                            <Button
                                                key={fix.key}
                                                size="small"
                                                variant="outlined"
                                                className={styles.emptyFixButton}
                                                onClick={() => patchFilters(fix.patch)}
                                                data-testid={`similar-empty-fix-${fix.key}`}
                                            >
                                                {fix.label}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
